import { logger } from "@/lib/logger";
import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Layers,
  Plus,
  Trash2,
  Zap,
  DollarSign,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Loader2,
  X,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem("roas_session_token");
  return token ? { "Authorization": `Bearer ${token}` } : {};
}

// Count-up from `from` to `to` with an ease-out curve — used for the impact preview reveal.
function AnimatedNumber({ from = 0, to = 0, decimals = 2, prefix = "", suffix = "", duration = 1000 }) {
  const [val, setVal] = useState(from);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const animate = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(from + (to - from) * eased);
      if (t < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [from, to, duration]);
  return <span>{prefix}{val.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</span>;
}

export function BudgetGroups({ campaigns = [], refreshSignal = 0 }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [optimizing, setOptimizing] = useState(null);
  const [newGroup, setNewGroup] = useState({ name: "", total_budget: "", optimization_frequency: "daily" });
  const [draggedCampaign, setDraggedCampaign] = useState(null);
  const [dragOverGroup, setDragOverGroup] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewGroupId, setPreviewGroupId] = useState(null);

  const fetchGroups = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/budget-groups`, { credentials: "include", headers: authHeaders() });
      if (resp.ok) setGroups(await resp.json());
    } catch (e) {
      logger.error("Failed to fetch budget groups:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups, refreshSignal]);

  const handleCreate = async () => {
    if (!newGroup.name || !newGroup.total_budget) {
      toast.error("Name and budget are required");
      return;
    }
    try {
      const resp = await fetch(`${API}/budget-groups`, {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ ...newGroup, total_budget: parseFloat(newGroup.total_budget) }),
      });
      if (resp.ok) {
        toast.success("Budget group created");
        setShowCreate(false);
        setNewGroup({ name: "", total_budget: "", optimization_frequency: "daily" });
        fetchGroups();
      } else {
        const err = await resp.json();
        toast.error(err.detail || "Failed to create");
      }
    } catch { toast.error("Failed to create budget group"); }
  };

  const handleDelete = async (groupId) => {
    try {
      const resp = await fetch(`${API}/budget-groups/${groupId}`, { method: "DELETE", credentials: "include", headers: authHeaders() });
      if (resp.ok) { toast.success("Budget group deleted"); fetchGroups(); }
    } catch { toast.error("Failed to delete"); }
  };

  const updateGroup = async (groupId, payload, successMsg) => {
    try {
      const resp = await fetch(`${API}/budget-groups/${groupId}`, {
        method: "PUT",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        if (successMsg) toast.success(successMsg);
        fetchGroups();
      } else {
        const err = await resp.json();
        toast.error(err.detail || "Update failed");
      }
    } catch { toast.error("Update failed"); }
  };

  const handleToggleAuto = (groupId, enabled) =>
    updateGroup(
      groupId,
      { auto_optimize: enabled },
      enabled ? "Auto-optimize enabled — runs on schedule and pushes budgets automatically" : "Auto-optimize disabled"
    );

  const handleFrequencyChange = (groupId, freq) =>
    updateGroup(groupId, { optimization_frequency: freq }, `Auto-optimize schedule set to ${freq}`);

  const handleOptimize = async (groupId) => {    setOptimizing(groupId);
    try {
      const resp = await fetch(`${API}/budget-groups/${groupId}/optimize`, { method: "POST", credentials: "include", headers: authHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        const pushResults = data.push_results || [];
        const pushed = pushResults.filter(r => r.success);
        const failed = pushResults.filter(r => !r.success);
        if (pushed.length > 0) {
          toast.success(`Optimized and synced ${pushed.length} campaign budgets to ad platforms`);
        } else if (failed.length > 0) {
          toast.warning(`Optimized locally. Platform sync failed: ${failed.map(f => f.detail).join(", ")}`);
        } else {
          toast.success("Budget optimized based on ROAS performance");
        }
        fetchGroups();
      } else {
        const err = await resp.json();
        toast.error(err.detail || "Optimization failed");
      }
    } catch { toast.error("Optimization failed"); }
    finally { setOptimizing(null); }
  };

  const handlePreview = async (groupId) => {
    setPreviewGroupId(groupId);
    setPreviewLoading(true);
    setShowPreview(true);
    setPreviewData(null);
    try {
      const resp = await fetch(`${API}/budget-groups/${groupId}/preview`, { method: "POST", credentials: "include", headers: authHeaders() });
      if (resp.ok) {
        setPreviewData(await resp.json());
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || "Failed to preview optimization");
        setShowPreview(false);
      }
    } catch {
      toast.error("Failed to preview optimization");
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApplyFromPreview = async () => {
    if (!previewGroupId) return;
    setShowPreview(false);
    await handleOptimize(previewGroupId);
    setPreviewData(null);
  };

  const handleRemoveCampaign = async (groupId, campaignId) => {
    try {
      const resp = await fetch(`${API}/budget-groups/${groupId}/campaigns/${campaignId}`, { method: "DELETE", credentials: "include", headers: authHeaders() });
      if (resp.ok) {
        toast.success("Campaign removed from group");
        fetchGroups();
      } else {
        toast.error("Failed to remove campaign");
      }
    } catch { toast.error("Failed to remove campaign"); }
  };

  const handleDrop = async (groupId) => {
    if (!draggedCampaign) return;
    setDragOverGroup(null);
    try {
      const resp = await fetch(`${API}/budget-groups/${groupId}/campaigns`, {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ campaign_ids: [draggedCampaign] }),
      });
      if (resp.ok) {
        toast.success("Campaign added to budget group");
        fetchGroups();
      } else {
        const err = await resp.json();
        toast.error(err.detail || "Failed to add campaign");
      }
    } catch { toast.error("Failed to add campaign"); }
    setDraggedCampaign(null);
  };

  const ungroupedCampaigns = campaigns.filter(
    (c) => !groups.some((g) => g.campaign_ids?.includes(c.campaign_id))
  );

  const getCampaignName = (cid) => {
    const c = campaigns.find((x) => x.campaign_id === cid);
    return c?.name || cid;
  };

  const getCampaignPlatform = (cid) => {
    const c = campaigns.find((x) => x.campaign_id === cid);
    return c?.platform || "";
  };

  const formatCurrency = (v) => `$${(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return null;

  return (
    <div data-testid="budget-groups" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-violet-400" />
          <h2 className="text-white font-semibold text-lg">Budget Groups</h2>
          <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30">{groups.length}</Badge>
        </div>
        <Button
          data-testid="create-budget-group-btn"
          onClick={() => setShowCreate(true)}
          size="sm"
          className="bg-violet-600 text-white hover:bg-violet-700"
        >
          <Plus className="w-4 h-4 mr-1" /> New Group
        </Button>
      </div>

      {/* Groups */}
      <AnimatePresence>
        {groups.map((group) => {
          const isExpanded = expandedGroup === group.group_id;
          const campaignCount = group.campaign_ids?.length || 0;

          return (
            <motion.div
              key={group.group_id}
              data-testid={`budget-group-${group.group_id}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              onDragOver={(e) => { e.preventDefault(); setDragOverGroup(group.group_id); }}
              onDragLeave={() => setDragOverGroup(null)}
              onDrop={() => handleDrop(group.group_id)}
              className={cn(
                "rounded-xl border transition-all",
                dragOverGroup === group.group_id
                  ? "border-violet-500/60 bg-violet-500/10"
                  : "border-white/10 bg-[#121212]"
              )}
            >
              {/* Group Header */}
              <div
                className="p-4 flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedGroup(isExpanded ? null : group.group_id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                    <Layers className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{group.name}</h3>
                    <div className="flex items-center gap-3 text-xs text-zinc-400">
                      <span>{formatCurrency(group.total_budget)} budget</span>
                      <span>{campaignCount}/10 campaigns</span>
                      <span className="capitalize">{group.optimization_frequency} optimization</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {group.last_optimized && (
                    <Badge className="bg-emerald-500/10 text-emerald-400 text-xs">Optimized</Badge>
                  )}
                  <div
                    className="flex items-center gap-1.5 mr-1"
                    onClick={(e) => e.stopPropagation()}
                    title="Automatically optimize on schedule and push budgets to ad platforms"
                  >
                    <span className={cn("text-[11px]", group.auto_optimize ? "text-emerald-400" : "text-zinc-400")}>Auto</span>
                    <Switch
                      data-testid={`auto-optimize-${group.group_id}-toggle`}
                      checked={!!group.auto_optimize}
                      onCheckedChange={(v) => handleToggleAuto(group.group_id, v)}
                    />
                  </div>
                  <Button
                    data-testid={`preview-${group.group_id}-btn`}
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); handlePreview(group.group_id); }}
                    disabled={campaignCount === 0}
                    className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                  >
                    <TrendingUp className="w-3 h-3 mr-1" /> Preview Impact
                  </Button>
                  <Button
                    data-testid={`optimize-${group.group_id}-btn`}
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleOptimize(group.group_id); }}
                    disabled={optimizing === group.group_id || campaignCount === 0}
                    className="bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/30"
                  >
                    {optimizing === group.group_id ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Zap className="w-3 h-3 mr-1" />
                    )}
                    Optimize
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); handleDelete(group.group_id); }}
                    className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                </div>
              </div>

              {/* Expanded Content */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 border-t border-white/5 pt-3 space-y-2">
                      {group.auto_optimize && (
                        <div
                          data-testid={`auto-optimize-${group.group_id}-info`}
                          className="flex items-center justify-between gap-3 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg"
                        >
                          <div className="flex items-center gap-2 text-emerald-300 text-xs">
                            <Zap className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>Auto-optimizes {group.optimization_frequency} — new budgets are pushed to your ad platforms automatically</span>
                          </div>
                          <Select
                            value={group.optimization_frequency}
                            onValueChange={(v) => handleFrequencyChange(group.group_id, v)}
                          >
                            <SelectTrigger
                              data-testid={`auto-optimize-${group.group_id}-frequency`}
                              className="w-28 h-8 bg-black/50 border-white/10 text-white text-xs flex-shrink-0"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#18181B] border-white/10">
                              <SelectItem value="hourly" className="text-white hover:bg-white/10">Hourly</SelectItem>
                              <SelectItem value="daily" className="text-white hover:bg-white/10">Daily</SelectItem>
                              <SelectItem value="weekly" className="text-white hover:bg-white/10">Weekly</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {campaignCount === 0 ? (
                        <div className="text-center py-6 text-zinc-400 text-sm border border-dashed border-white/10 rounded-lg">
                          Drag campaigns here to add them to this group
                        </div>
                      ) : (
                        group.campaign_ids.map((cid) => {
                          const alloc = group.allocations?.[cid] || 0;
                          const pct = group.total_budget > 0 ? (alloc / group.total_budget) * 100 : 0;
                          return (
                            <div key={cid} className="flex items-center justify-between p-3 bg-white/5 rounded-lg group">
                              <div className="flex items-center gap-3">
                                <Badge className="bg-zinc-700 text-zinc-300 text-xs capitalize">{getCampaignPlatform(cid)}</Badge>
                                <span className="text-zinc-300 text-sm">{getCampaignName(cid)}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="w-24 h-1.5 bg-white/10 rounded-full overflow-hidden">
                                  <div className="h-full bg-violet-500 rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
                                </div>
                                <span className="text-white text-sm font-medium w-20 text-right">{formatCurrency(alloc)}</span>
                                <span className="text-zinc-400 text-xs w-12 text-right">{pct.toFixed(0)}%</span>
                                <Button
                                  data-testid={`remove-campaign-${cid}`}
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => { e.stopPropagation(); handleRemoveCampaign(group.group_id, cid); }}
                                  className="text-red-400 border-red-500/30 hover:bg-red-500/20 hover:text-red-300 px-2 py-1 h-7 opacity-60 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3 mr-1" />
                                  Remove
                                </Button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Ungrouped Campaigns (draggable) */}
      {groups.length > 0 && ungroupedCampaigns.length > 0 && (
        <div className="mt-4">
          <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">Drag campaigns to a group above</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {ungroupedCampaigns.map((c) => (
              <div
                key={c.campaign_id}
                draggable
                onDragStart={() => setDraggedCampaign(c.campaign_id)}
                onDragEnd={() => { setDraggedCampaign(null); setDragOverGroup(null); }}
                className="flex items-center gap-2 p-2.5 bg-white/5 border border-white/10 rounded-lg cursor-grab active:cursor-grabbing hover:border-violet-500/30 transition-colors"
              >
                <GripVertical className="w-3 h-3 text-zinc-500 flex-shrink-0" />
                <Badge className="bg-zinc-700 text-zinc-300 text-xs capitalize flex-shrink-0">{c.platform}</Badge>
                <span className="text-zinc-300 text-sm truncate">{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {groups.length === 0 && (
        <div className="text-center py-8 border border-dashed border-white/10 rounded-xl">
          <Layers className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
          <p className="text-zinc-400 text-sm">No budget groups yet</p>
          <p className="text-zinc-500 text-xs mt-1">Create a group to start optimizing campaign budgets</p>
        </div>
      )}

      {/* Optimization Impact Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={(o) => { setShowPreview(o); if (!o) setPreviewData(null); }}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-400" />
              Optimization Impact Preview
            </DialogTitle>
          </DialogHeader>

          {previewLoading || !previewData ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
            </div>
          ) : (
            <div data-testid="optimization-preview" className="space-y-5 py-2">
              <p className="text-zinc-400 text-sm">
                Reallocating <span className="text-white font-medium">{formatCurrency(previewData.total_budget)}/day</span> across{" "}
                <span className="text-white font-medium">{previewData.campaigns.length}</span> campaigns in{" "}
                <span className="text-white font-medium">{previewData.group_name}</span>:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-widest text-zinc-400">Blended ROAS</span>
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-zinc-500 text-lg line-through">{previewData.before.roas.toFixed(2)}x</span>
                    <ArrowRight className="w-4 h-4 text-zinc-500 self-center" />
                    <span data-testid="preview-roas-after" className="text-emerald-400 font-heading text-3xl font-bold">
                      <AnimatedNumber from={previewData.before.roas} to={previewData.after.roas} decimals={2} suffix="x" />
                    </span>
                  </div>
                  {previewData.deltas.roas_pct != null && (
                    <p className="text-emerald-400 text-sm mt-1 font-medium">
                      {previewData.deltas.roas_pct >= 0 ? "▲" : "▼"} {Math.abs(previewData.deltas.roas_pct)}% return on ad spend
                    </p>
                  )}
                </div>

                <div className="rounded-xl border border-blue-500/25 bg-blue-500/5 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-widest text-zinc-400">Cost per Acquisition</span>
                    <TrendingDown className="w-4 h-4 text-blue-400" />
                  </div>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-zinc-500 text-lg line-through">
                      {previewData.before.cac != null ? `$${previewData.before.cac.toFixed(2)}` : "—"}
                    </span>
                    <ArrowRight className="w-4 h-4 text-zinc-500 self-center" />
                    <span data-testid="preview-cac-after" className="text-blue-400 font-heading text-3xl font-bold">
                      {previewData.after.cac != null ? (
                        <AnimatedNumber from={previewData.before.cac ?? previewData.after.cac} to={previewData.after.cac} decimals={2} prefix="$" />
                      ) : "—"}
                    </span>
                  </div>
                  {previewData.deltas.cac_pct != null && (
                    <p className="text-blue-400 text-sm mt-1 font-medium">
                      {previewData.deltas.cac_pct <= 0 ? "▼" : "▲"} {Math.abs(previewData.deltas.cac_pct)}% cost per customer
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                  <span className="text-xs text-zinc-400">Projected daily revenue</span>
                  <p className="text-white font-semibold text-base mt-1">
                    {formatCurrency(previewData.before.revenue)} <ArrowRight className="w-3 h-3 inline text-zinc-500" />{" "}
                    <span className="text-emerald-400">{formatCurrency(previewData.after.revenue)}</span>
                  </p>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                  <span className="text-xs text-zinc-400">Projected conversions</span>
                  <p className="text-white font-semibold text-base mt-1">
                    {previewData.before.conversions.toFixed(0)} <ArrowRight className="w-3 h-3 inline text-zinc-500" />{" "}
                    <span className="text-emerald-400">{previewData.after.conversions.toFixed(0)}</span>
                  </p>
                </div>
              </div>

              <div>
                <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">Budget reallocation</p>
                <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                  {previewData.campaigns.map((c) => (
                    <div key={c.campaign_id} className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex items-center gap-2 min-w-0">
                        <Badge className="bg-zinc-700 text-zinc-300 text-xs capitalize flex-shrink-0">{c.platform}</Badge>
                        <span className="text-zinc-300 text-sm truncate">{c.name}</span>
                        <span className="text-zinc-500 text-xs flex-shrink-0">ROAS {c.roas}x</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-zinc-400 text-sm">{formatCurrency(c.old_budget)}</span>
                        <ArrowRight className="w-3 h-3 text-zinc-500" />
                        <span className={cn("text-sm font-medium", c.new_budget > c.old_budget ? "text-emerald-400" : c.new_budget < c.old_budget ? "text-red-400" : "text-white")}>
                          {formatCurrency(c.new_budget)}
                        </span>
                        {c.change_pct != null && (
                          <span className={cn("text-xs w-14 text-right", c.change_pct > 0 ? "text-emerald-400" : c.change_pct < 0 ? "text-red-400" : "text-zinc-500")}>
                            {c.change_pct > 0 ? "+" : ""}{c.change_pct}%
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-[11px] text-zinc-500">
                Projected from each campaign's historical efficiency (revenue &amp; conversions per dollar) at the same total budget. Actual results vary with market conditions.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => { setShowPreview(false); setPreviewData(null); }} className="text-zinc-400">Close</Button>
            <Button
              data-testid="apply-optimization-btn"
              onClick={handleApplyFromPreview}
              disabled={!previewData || optimizing === previewGroupId}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
            >
              <Zap className="w-4 h-4 mr-2" /> Apply Optimization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-violet-400" />
              Create Budget Group
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-zinc-400">Group Name</Label>
              <Input
                data-testid="bg-name-input"
                value={newGroup.name}
                onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                placeholder="e.g. Q1 Campaign Bundle"
                className="bg-black/50 border-white/10 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Total Budget ($)</Label>
              <Input
                data-testid="bg-budget-input"
                type="number"
                value={newGroup.total_budget}
                onChange={(e) => setNewGroup({ ...newGroup, total_budget: e.target.value })}
                placeholder="5000"
                className="bg-black/50 border-white/10 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Optimization Frequency</Label>
              <Select
                value={newGroup.optimization_frequency}
                onValueChange={(v) => setNewGroup({ ...newGroup, optimization_frequency: v })}
              >
                <SelectTrigger className="bg-black/50 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181B] border-white/10">
                  <SelectItem value="hourly" className="text-white hover:bg-white/10">Hourly</SelectItem>
                  <SelectItem value="daily" className="text-white hover:bg-white/10">Daily</SelectItem>
                  <SelectItem value="weekly" className="text-white hover:bg-white/10">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-zinc-400">Cancel</Button>
            <Button data-testid="bg-create-btn" onClick={handleCreate} className="bg-violet-600 text-white hover:bg-violet-700">Create Group</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
